if (!customElements.get('laley-media-feature')) {
  customElements.define('laley-media-feature', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-laley-media-toggle]');
      if (!this.video || this.dataset.laleyMediaReady === 'true') return;

      this.dataset.laleyMediaReady = 'true';
      this.reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;
      this.userPaused = false;
      this.userStarted = false;
      this.inView = this.isNearViewport();
      this.externallyActive = this.closest('[data-laley-carousel-slide]')?.getAttribute('aria-hidden') !== 'true';

      this.prepareVideo();
      this.onReadiness = () => this.syncPlayback();
      this.onVisibility = () => this.syncPlayback();
      this.onPageShow = () => this.syncPlayback();
      this.onStateChange = () => this.syncState();

      ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((eventName) => {
        this.video.addEventListener(eventName, this.onReadiness);
      });
      ['play', 'playing', 'pause', 'waiting', 'ended', 'error', 'emptied'].forEach((eventName) => {
        this.video.addEventListener(eventName, this.onStateChange);
      });
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('pageshow', this.onPageShow);

      if (this.toggle) {
        this.onToggle = () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.userStarted = true;
            this.tryPlay();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        };
        this.toggle.addEventListener('click', this.onToggle);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new window.IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio > 0.05;
          this.syncPlayback();
        }, { rootMargin: '160px 0px', threshold: [0, 0.05, 0.25] });
        this.observer.observe(this);
      }
      this.syncPlayback();
      this.syncState();
    }

    disconnectedCallback() {
      this.clearPlayTimers();
      this.observer?.disconnect();
      this.toggle?.removeEventListener('click', this.onToggle);
      if (!this.video) return;

      ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((eventName) => {
        this.video.removeEventListener(eventName, this.onReadiness);
      });
      ['play', 'playing', 'pause', 'waiting', 'ended', 'error', 'emptied'].forEach((eventName) => {
        this.video.removeEventListener(eventName, this.onStateChange);
      });
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pageshow', this.onPageShow);
      delete this.dataset.laleyMediaReady;
    }

    prepareVideo() {
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.autoplay = true;
      this.video.loop = true;
      this.video.controls = false;
      this.video.playsInline = true;
      this.video.disablePictureInPicture = true;
      this.video.setAttribute('muted', '');
      this.video.setAttribute('autoplay', '');
      this.video.setAttribute('loop', '');
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
      this.video.setAttribute('disablepictureinpicture', '');
      this.video.removeAttribute('controls');
    }

    isNearViewport() {
      const rect = this.getBoundingClientRect();
      return rect.width > 0
        && rect.height > 0
        && rect.bottom > -160
        && rect.top < window.innerHeight + 160;
    }

    setActive(active, allowMotion = false) {
      this.externallyActive = Boolean(active);
      if (allowMotion) this.userStarted = true;
      if (!this.video) return;
      this.syncPlayback();
    }

    shouldPlay() {
      return this.externallyActive
        && this.inView
        && !document.hidden
        && !this.userPaused
        && (!this.reduceMotion || this.userStarted);
    }

    syncPlayback() {
      if (!this.video) return;
      if (!this.shouldPlay()) {
        this.clearPlayTimers();
        if (!this.video.paused) this.video.pause();
        this.syncState();
        return;
      }

      this.tryPlay();
      this.schedulePlayRetries();
    }

    tryPlay() {
      if (!this.shouldPlay()) return;
      this.prepareVideo();
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt
          .then(() => {
            delete this.dataset.laleyMediaAutoplayBlocked;
            this.syncState();
          })
          .catch(() => {
            this.dataset.laleyMediaAutoplayBlocked = 'true';
            this.syncState();
          });
      }
    }

    schedulePlayRetries() {
      if (this.playTimers?.length) return;
      const delays = [150, 500, 1200];
      this.playTimers = delays.map((delay, index) => window.setTimeout(() => {
        if (this.video.paused) this.tryPlay();
        if (index === delays.length - 1) this.playTimers = [];
      }, delay));
    }

    clearPlayTimers() {
      this.playTimers?.forEach((timer) => window.clearTimeout(timer));
      this.playTimers = [];
    }

    syncState() {
      if (!this.video) return;
      const isPaused = this.video.paused;
      const isPlaying = !isPaused && !this.video.ended && this.video.readyState >= 2;
      this.classList.toggle('is-video-playing', isPlaying);
      this.classList.toggle('is-video-fallback', !isPlaying);
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(!isPaused));
      this.toggle.setAttribute('aria-label', isPaused ? 'Spill av video' : 'Sett video på pause');
      this.toggle.querySelector('[data-laley-media-pause]')?.toggleAttribute('hidden', isPaused);
      this.toggle.querySelector('[data-laley-media-play]')?.toggleAttribute('hidden', !isPaused);
    }
  });
}
